import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { useCallback, useMemo } from 'react';

/**
 * Props to spread on an interactive element (input, button, select, ...)
 * nested inside a dnd-kit drag handle — e.g. a `useSortable` wrapper, or any
 * ancestor carrying dnd-kit's `listeners`/`attributes`.
 */
export interface DndContainmentProps {
  onClick: (e: ReactMouseEvent) => void;
  onKeyDown: (e: ReactKeyboardEvent) => void;
  onPointerDown: (e: ReactPointerEvent) => void;
}

/**
 * Stops pointer/keyboard/click events from bubbling up to a dnd-kit drag
 * handle, so an interactive control nested inside one behaves normally
 * instead of triggering (or being swallowed by) drag activation.
 *
 * `onKeyDown` stops propagation for EVERY key, not just Space/Enter —
 * dnd-kit's `KeyboardSensor` (attached via `useSortable` listeners on an
 * ancestor) treats Space/Enter as drag-activation keys. If only those two
 * are stopped, every other key still bubbles fine, but the moment the
 * nested control needs Space or Enter itself (a rename input, a textarea)
 * the sensor intercepts it before the control sees it. See
 * `PipelineColumn.tsx` (`handleEditKeyDown`) for the rationale, and
 * `PipelineColumn.inlineRename.test.tsx` for a regression test that
 * reproduces the bug when propagation isn't stopped for all keys.
 *
 * Stopping propagation is NOT the same as preventing default: this helper
 * never calls `preventDefault()`, so arrows, Tab, Escape, and every other
 * key still reach the nested control and behave exactly as that control
 * expects (e.g. Escape cancelling an inline edit, Tab moving focus).
 *
 * Containment handled here is necessary but not sufficient for every
 * consumer:
 * - `PipelineBoard` additionally suppresses any `click` within
 *   `POST_DRAG_CLICK_SUPPRESSION_MS` (250ms) of a drag ending
 *   (`PipelineBoard.tsx:421-432`, `:135`) — a drag that releases inside the
 *   element it started on fires a trailing synthetic click after dnd-kit's
 *   teardown, which this helper does not (and cannot, since it runs before
 *   that click exists) filter out on its own.
 * - `DragOverlay` renders `renderCard(activeItem, true)` directly, outside
 *   `PipelineCard` (`PipelineBoard.tsx:966-976`) — so any interactive
 *   control opened via this helper (e.g. an inline editor) will NOT be
 *   remounted/reset by the overlay when a drag starts. Consumers must close
 *   their own open editors on drag start rather than relying on the overlay
 *   to do it for them.
 */
export function useDndContainment(): DndContainmentProps {
  const onClick = useCallback((e: ReactMouseEvent) => {
    e.stopPropagation();
  }, []);

  const onKeyDown = useCallback((e: ReactKeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const onPointerDown = useCallback((e: ReactPointerEvent) => {
    e.stopPropagation();
  }, []);

  return useMemo(
    () => ({ onClick, onKeyDown, onPointerDown }),
    [onClick, onKeyDown, onPointerDown]
  );
}
